export interface PartialEliminationRoundSlot {
  matchIndex: number | null;
  directAdvancerIndex: number | null;
}

const DIRECT_ADVANCER_IDS_MARKER = "[direct_advancer_ids=";

export function directAdvancerIdsFromNotes(notesValues: Array<string | null | undefined>): number[] | null {
  for (const notes of notesValues) {
    const start = notes?.indexOf(DIRECT_ADVANCER_IDS_MARKER) ?? -1;
    if (start < 0 || !notes) continue;
    const valuesStart = start + DIRECT_ADVANCER_IDS_MARKER.length;
    const end = notes.indexOf("]", valuesStart);
    if (end < 0) continue;
    const ids = notes.slice(valuesStart, end).split(",").map(Number);
    if (ids.length > 0 && ids.every((id) => Number.isInteger(id) && id > 0)) return ids;
  }
  return null;
}

function standardMatchAnchors(matchCount: number): number[] {
  if (matchCount <= 1) return [1];
  let anchors = [1, 2];
  while (anchors.length < matchCount) {
    const size = anchors.length * 2;
    const expanded: number[] = [];
    anchors.forEach((seed, index) => {
      const partner = size + 1 - seed;
      expanded.push(...(index % 2 === 0 ? [seed, partner] : [partner, seed]));
    });
    anchors = expanded;
  }
  return anchors;
}

/**
 * Place a partial configured round inside its full standard bracket.
 * Example: 13 players in R16 => 5 real matches and seeds 1-3 direct.
 */
export function partialEliminationRoundSlots(
  fullMatchCount: number,
  directAdvancerCount: number,
): PartialEliminationRoundSlot[] {
  const fieldSize = fullMatchCount * 2;
  return standardMatchAnchors(fullMatchCount).map((anchor) => {
    const opponent = fieldSize + 1 - anchor;
    const directSeed = [anchor, opponent].find((seed) => seed <= directAdvancerCount) ?? null;
    if (directSeed !== null) {
      return { matchIndex: null, directAdvancerIndex: directSeed - 1 };
    }
    return {
      matchIndex: Math.min(anchor, opponent) - directAdvancerCount - 1,
      directAdvancerIndex: null,
    };
  });
}

function partialRoundSourceForSeed(
  seed: number,
  directAdvancerCount: number,
): PartialEliminationRoundSlot {
  return seed <= directAdvancerCount
    ? { matchIndex: null, directAdvancerIndex: seed - 1 }
    : { matchIndex: seed - directAdvancerCount - 1, directAdvancerIndex: null };
}

/** Sources for the next round, oriented exactly like the backend seed template. */
export function partialEliminationNextRoundSources(
  fullMatchCount: number,
  directAdvancerCount: number,
): [PartialEliminationRoundSlot, PartialEliminationRoundSlot][] {
  const nextRoundFieldSize = fullMatchCount;
  return standardMatchAnchors(fullMatchCount / 2).map((anchor) => [
    partialRoundSourceForSeed(anchor, directAdvancerCount),
    partialRoundSourceForSeed(nextRoundFieldSize + 1 - anchor, directAdvancerCount),
  ]);
}

export function partialEliminationPreviewStructure(
  drawSize: number,
  directAdvancerCount: number,
): {
  initialSlots: PartialEliminationRoundSlot[];
  nextRoundSources: [PartialEliminationRoundSlot, PartialEliminationRoundSlot][];
} {
  const structuralMatchCount = drawSize / 2;
  return {
    initialSlots: partialEliminationRoundSlots(structuralMatchCount, directAdvancerCount),
    nextRoundSources: partialEliminationNextRoundSources(structuralMatchCount, directAdvancerCount),
  };
}

export function isPartialRoundOf16Preview(
  drawSize: number,
  participantCount: number,
  directAdvancerCount: number,
  playInCount: number,
): boolean {
  return drawSize === 16
    && participantCount <= drawSize
    && participantCount > drawSize / 2
    && directAdvancerCount > 0
    && playInCount > 0;
}

/**
 * Tamaño efectivo del bracket previo. Espeja al backend
 * (matches/services.py: si los inscritos caben en la mitad del bracket
 * configurado, se ajusta a la potencia de 2 mínima). Sin esto, 6 jugadores
 * con Ronda 128 calculaban 3 cupos de ronda objetivo y el previo salía vacío.
 */
export function effectivePreviewDrawSize(configuredSize: number | null | undefined, participantCount: number): number {
  let minimal = 2;
  while (minimal < participantCount) minimal *= 2;
  const configured = configuredSize && configuredSize >= 2 ? configuredSize : minimal;
  return participantCount > configured / 2 ? configured : minimal;
}
